﻿import { Alert, AlertTitle, Box, Button, Card, CardContent, CardHeader, CircularProgress, Container, Divider, FormControlLabel, FormLabel, Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Switch, SxProps, Typography } from "@mui/material"
import * as React from "react";
import { Link } from 'react-router-dom';
import { AdminRoutes } from "../../constants/routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faUsers, faSquare as faSquareSolid } from "@fortawesome/pro-solid-svg-icons";
import AdminNavigation from "./AdminNavigation";
import useCertificateRegistrations from "@/hooks/useCertificateRegistrations";
import { faCheck, faSquareCheck, faSquare, faSquareMinus } from "@fortawesome/pro-duotone-svg-icons";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { FormsFeatureContext } from "@ngt/forms";
import { GetRegistrationsValidForCertificatesResponse, QueueCertificates } from "@/api/dtos";
import { DateTime } from "luxon";

const cardStyleProps: SxProps = {
    '&:hover': {
        backgroundColor: '#ededed',
        cursor: 'pointer'
    },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
}

const AdminCertificatesForm: React.FunctionComponent = () => {
    const {serviceStackClient} = useContext(FormsFeatureContext);

    const [ valid, setValid ] = useState(false);
    const [ receivedEmail, setReceivedEmail ] = useState(false);
    const [ requestedCertificate, setRequestedCertificate ] = useState(false);
    
    const { registrations, mutate: mutateRegistrations } = useCertificateRegistrations();
    const [ selected, setSelected ] = useState<Record<number, boolean>>({});

    const [ sending, setSending ] = useState<boolean>(false);


    const filteredRegistrations = useMemo(() => {
        return registrations?.filter(registration => {
            
            if (!receivedEmail && !!registration.certificateDate) {
                return false;
            }

            if (!requestedCertificate && !registration.receiveCertificate) {
                return false;
            }

            if (
                !valid && (
                    (registration.amountOwing ?? 0) > 0 ||
                    (registration.attendanceType !== 2 && !registration.registered)
                )
            ) 
            {
                return false;
            }

            return true;
        });
    }, [registrations, receivedEmail, requestedCertificate, valid]);

    useEffect(() => {
        setSelected(c => {
            const newSelected = {...c};

            for (let id in newSelected) {
                if (!newSelected[id]) {
                    continue;
                }
    
                const registration = filteredRegistrations?.find(x => x.id === +id)
    
                if (!registration)
                {
                    newSelected[id] = false;
                    continue;
                }
    
                if (!!registration.certificatePending) {
                    newSelected[id] = false;
                    continue;
                }
            }

            return newSelected;
        })

    }, [setSelected, filteredRegistrations])

    useEffect(() => {
        const intervalId = setInterval(async () => {
            await mutateRegistrations(new GetRegistrationsValidForCertificatesResponse({ registrations }), true);
        }, 5000);

        return () => {
            clearInterval(intervalId);
        }
    }, [mutateRegistrations, registrations])

    const allSelected = useMemo(() => {
        if (!filteredRegistrations || filteredRegistrations.length === 0) {
            return false;
        }

        return filteredRegistrations?.reduce<boolean>((newSelected, x) => {
            if (!selected[x.id!])
            {
                return false;
            }

            return newSelected;
        }, true) ?? false;
    }, [selected, filteredRegistrations])

    const hasSelected = useMemo(() => {
        return filteredRegistrations?.reduce<boolean>((newSelected, x) => {
            if (selected[x.id!])
            {
                return true;
            }

            return newSelected;
        }, false) ?? false;
    }, [selected, filteredRegistrations])

    const selectAll = () => {
        setSelected(filteredRegistrations?.reduce<Record<number, boolean>>((newSelected, x) => {
            newSelected[x.id!] = true;

            return newSelected;
        }, {}) ?? {})
    }

    const unselectAll = () => {
        setSelected({})
    }

    const send = async () => {
        setSending(true);


        const registrationIds = filteredRegistrations?.reduce<number[]>((ids, x) => {
            if (selected[x.id!])
            {
                return [...ids, x.id!]
            }

            return ids;
        }, []);

        await serviceStackClient.post(new QueueCertificates({ registrationIds }))

        unselectAll();

        await mutateRegistrations(new GetRegistrationsValidForCertificatesResponse({ registrations }), true);

        setSending(false);
    };

    const toggleReceivedEmail = (checked: boolean) => {
        setReceivedEmail(checked);
    }

    const toggleRequestedCertificate = (checked: boolean) => {
        setRequestedCertificate(checked);
    }

    const criteriaCount = (requestedCertificate ? 1 : 0) + (receivedEmail ? 1 : 0) + (valid ? 1 : 0);
    const minHeight = criteriaCount === 3 ? 
        '88px' : 
        criteriaCount === 2 ?
            '64px' :
            '48px'

    return (
        <Box>
            <AdminNavigation/>
            <Box>
                <Container
                    maxWidth="lg"
                >
                    <Typography variant="h2" sx={{my: 3}}>
                        Certificates
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Button
                            sx={{ mb: 3 }}
                            startIcon={
                                <FontAwesomeIcon icon={allSelected ? faSquareCheck : hasSelected ? faSquareMinus : faSquare} />
                            }
                            onClick={allSelected ? unselectAll : selectAll}
                            variant="contained"
                            disabled={sending || !filteredRegistrations || filteredRegistrations.length === 0}
                        >
                            {allSelected ? 'Unselect All' : 'Select All'}
                        </Button>
                        <Box sx={{mt: -5, textAlign: 'right'}}>
                            <Box>
                                <FormControlLabel 
                                    labelPlacement="start" 
                                    control={
                                        <Switch 
                                            size="small"
                                            value={valid} 
                                            onChange={(e, checked) => setValid(checked)} 
                                            disabled={sending}
                                        />
                                    } 
                                    label="Show registrations that do not meet attendance requirements?" 
                                />
                            </Box>
                            <Box>
                                <FormControlLabel 
                                    labelPlacement="start" 
                                    control={
                                        <Switch 
                                            size="small"
                                            value={receivedEmail} 
                                            onChange={(e, checked) => toggleReceivedEmail(checked)} 
                                            disabled={sending}
                                        />
                                    } 
                                    label="Show attendees who already received a certificate?" 
                                />
                            </Box>
                            <Box>
                                <FormControlLabel 
                                    labelPlacement="start"  
                                    control={
                                        <Switch 
                                            size="small"
                                            value={requestedCertificate} 
                                            onChange={(e, checked) => toggleRequestedCertificate(checked)}
                                            disabled={sending}
                                        />
                                    } 
                                    label="Show attendees who did not request a certificate?" 
                                />
                            </Box>
                        </Box>
                    </Box>
                    
                    {
                        !!filteredRegistrations && filteredRegistrations.length > 0 && (
                            <Paper>
                                <List
                                    disablePadding
                                >
                                    {
                                        filteredRegistrations?.map(x => {
                                            return (
                                                <Fragment
                                                    key={x.id!}
                                                >
                                                    <ListItem
                                                        disablePadding
                                                        secondaryAction={
                                                            criteriaCount !== 0 &&
                                                            <Box
                                                                sx={{
                                                                    backgroundColor: 'grey.100',
                                                                    mr: -2,
                                                                    px: 2,
                                                                    minHeight: minHeight,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'stretch',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                {
                                                                    valid && (
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                gap: 2
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    minWidth: '260px',
                                                                                    maxWidth: '260px'
                                                                                }}
                                                                            >
                                                                                Meets Attendance Requirements:
                                                                            </Box>
                                                                            <Box
                                                                                sx={{
                                                                                    minWidth: '250px',
                                                                                    maxWidth: '250px'
                                                                                }}
                                                                            >
                                                                                {
                                                                                    (x.amountOwing ?? 0) > 0 ? 
                                                                                            <>No &ndash; Amount Owing</> :
                                                                                        (x.attendanceType !== 2 && !x.registered) ? 
                                                                                            <>No &ndash; Attendance Not Marked</>  : 
                                                                                            <>Yes</> 
                                                                                }
                                                                            </Box>
                                                                        </Box>
                                                                    )
                                                                }
                                                                {
                                                                    receivedEmail && (
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                gap: 2
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    minWidth: '260px',
                                                                                    maxWidth: '260px'
                                                                                }}
                                                                            >
                                                                                Certificate Send Date:
                                                                            </Box>
                                                                            <Box
                                                                                sx={{
                                                                                    minWidth: '250px',
                                                                                    maxWidth: '250px'
                                                                                }}
                                                                            >
                                                                                {x.certificateDate ? DateTime.fromISO(x.certificateDate).toFormat('dd/MM/yyyy')  : 'Not Sent' }
                                                                            </Box>
                                                                        </Box>
                                                                    )
                                                                }
                                                                {
                                                                    requestedCertificate && (
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                gap: 2
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    minWidth: '260px',
                                                                                    maxWidth: '260px'
                                                                                }}
                                                                            >
                                                                                Requested Certificate: 
                                                                            </Box>
                                                                            <Box
                                                                                sx={{
                                                                                    minWidth: '250px',
                                                                                    maxWidth: '250px'
                                                                                }}
                                                                            >
                                                                                {x.receiveCertificate ? 'Yes' : 'No' }
                                                                            </Box>
                                                                        </Box>
                                                                    )
                                                                }
                                                            </Box>
                                                        }
                                                    >
                                                        <ListItemButton
                                                            sx={{
                                                                minHeight: minHeight
                                                            }}
                                                            disabled={sending || x.certificatePending}
                                                            onClick={() => {
                                                                setSelected(c => ({...c, [x.id!]: !c[x.id!] }));
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{ color: selected[x.id!] ? 'success.main' : 'error.main'}}>
                                                                {
                                                                    x.certificatePending ?
                                                                        <CircularProgress size={24} /> :
                                                                        <FontAwesomeIcon size="lg" icon={selected[x.id!] ? faSquareCheck : faSquare} />
                                                                }
                                                            </ListItemIcon>
                                                            <ListItemText 
                                                                primary={<>{x.firstName} {x.lastName}</>}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                    <Divider />
                                                </Fragment>
                                            );
                                        })
                                    }
                                </List>
                            </Paper>
                        )
                    }
                    {
                        (!filteredRegistrations || filteredRegistrations.length === 0) && (
                            <Alert
                                severity="warning"
                            >
                                <AlertTitle>No Registrations Found</AlertTitle>
                                There were no registrations that match the selected criteria.
                            </Alert>
                        )
                    }
                    <LoadingButton
                        sx={{my: 3}}
                        disabled={!hasSelected}
                        loading={sending}
                        variant="contained"
                        onClick={send}
                    >
                        Send
                    </LoadingButton>
                </Container>
            </Box>
        </Box>
    )
}

export default AdminCertificatesForm